import { IApprovalForm, IApprovalManagers, IApprovalTemplateFields, IAssignableUsers, IRequisitionForm, IRequisitionPublicLink } from './types';
import { IApprovalPublicLink, IApprovalTemplate, IApprovalTemplateCreators } from "./ApprovalTemplates/types"
import { IRequestParams } from './types';
import {
  IApprovalFormRequestParams,
  IApprovalPublicLinkRequestParams
} from './ApprovalTemplates/types';
import RecruitApiClient from '../../RecruitApiClient';
import { IJob } from '../Jobs/types';

const BASE_URL = '/api/v4';

class Api {
  getEntityApprovers = () =>
    RecruitApiClient.get<{ entity_default_approvers: string[] }>(`${BASE_URL}/requisition_managers/entity_default_approvers`);
  getApprovalManagers = (params: {}) =>
    RecruitApiClient.get<{ requisition_managers: IApprovalManagers[] }>(`${BASE_URL}/requisition_managers`, params);
  createApprovalManager = (params: { requisition_manager: { name: string; email: string } }) =>
    RecruitApiClient.post<{ success: boolean }>(`${BASE_URL}/requisition_managers`, params);
  deleteApprovalManager = (approvalManagerId: number) =>
    RecruitApiClient.delete<{ success: boolean }>(`${BASE_URL}/requisition_managers/${approvalManagerId}`);
  deleteApprovalManagers = (params: { requisition_manager_ids: number[] }) =>
    RecruitApiClient.delete<{ requisition_manager_ids: { failure: string[] }}>(`${BASE_URL}/requisition_managers/bulk_destroy`, params);
  updateApprovalManager = (
    params: { requisition_manager: { name: string; email: string } },
    approvalManagerId: number
  ) => RecruitApiClient.put<{ success: string }>(`${BASE_URL}/requisition_managers/${approvalManagerId}`, params);
  getEmailTemplates = () => RecruitApiClient.get<{ email_templates: [string, Array<{ id: number; name: string }>][] }>(`${BASE_URL}/requisition_forms/email_templates`);
  getEnabledApprovalTemplates = () =>
    RecruitApiClient.get<{ enabled_requisition_forms: [] }>(`${BASE_URL}/requisition_forms/enabled_for_applications`);
  createApprovalTemplate = (params: { requisition_form: IRequisitionForm }) =>
    RecruitApiClient.post<{ id: string, link: string }>(`${BASE_URL}/requisition_forms`, params);
  updateApprovalTemplate = (
    approvalTemplateId: number,
    params: { requisition_form: IRequisitionForm }
  ) => RecruitApiClient.put(`${BASE_URL}/requisition_forms/${approvalTemplateId}`, params);
  cloneApprovalTemplate = (
    approvalTemplateId: number,
    params: { requisition_form: IRequisitionForm }
  ) => RecruitApiClient.post<{ success: string, redirect_url: string }>(`${BASE_URL}/requisition_forms/${approvalTemplateId}/clone`, params);
  getApprovalTemplate = (approvalTemplateId: number) =>
    RecruitApiClient.get<IApprovalTemplate>(`${BASE_URL}/requisition_forms/${approvalTemplateId}`);
  getApprovalTemplates = (params: IApprovalFormRequestParams) =>
    RecruitApiClient.get<{ requisition_forms: IApprovalTemplate[] }>(`${BASE_URL}/requisition_forms`, params);
  getApprovalForms = (params: IRequestParams) =>
    RecruitApiClient.get<{ requisitions: IApprovalForm[] }>(`${BASE_URL}/requisitions`, params);
  getApprovalForm = (approvalFormId: number) =>
    RecruitApiClient.get<IApprovalForm>(`${BASE_URL}/requisitions/${approvalFormId}`);
  putApprovalForm = (approvalFormId: number, params: Record<string, unknown>) =>
    RecruitApiClient.put<{ success: string }>(`${BASE_URL}/requisitions/${approvalFormId}`, params);
  postResendEmail = (approvalFormId: number, params: { approver_id: number }) =>
    RecruitApiClient.post(`${BASE_URL}/requisitions/${approvalFormId}/resend_email`, params);
  cloneApprovalForm = (approvalFormId: number) =>
    RecruitApiClient.post<{ success: string, url: string }>(`${BASE_URL}/requisitions/${approvalFormId}/clone`);
  generateBulkPdf = (approvalFormId: number) =>
    RecruitApiClient.post<{ success: string }>(`${BASE_URL}/requisitions/${approvalFormId}/bulk_pdf`);
  getTargetEntities = () => RecruitApiClient.get<{ entities: { id: string, name: string }[] }>(`${BASE_URL}/target_entities`);
  moveApprovalTemplate = (approvalTemplateId: number, entityId: string) =>
    RecruitApiClient.post(
      `${BASE_URL}/requisition_forms/${approvalTemplateId}/move?requisition_form[entity_id]=${entityId}`
    );
  getCreators = () => RecruitApiClient.get<{ creators: IApprovalTemplateCreators[] }>(`${BASE_URL}/requisition_forms/creators`);
  getApprovalPublicLinks = (params: IApprovalPublicLinkRequestParams) =>
    RecruitApiClient.get<{ requisition_public_links: IApprovalPublicLink[] }>(`${BASE_URL}/requisition_public_links`, params);
  createApprovalPublicLink = (params: { requisition_public_link: IRequisitionPublicLink }) =>
    RecruitApiClient.post<{ success: string }>(`${BASE_URL}/requisition_public_links`, params);
  updateApprovalPublicLink = (
    publicLinkId: number,
    params: { requisition_public_link: IRequisitionPublicLink }
  ) => RecruitApiClient.put<{ success: string }>(`${BASE_URL}/requisition_public_links/${publicLinkId}`, params);
  deleteApprovalPublicLinks = (params: {}) =>
    RecruitApiClient.delete<{ requisition_public_link_ids: { failure: [], success: [] } }>(`${BASE_URL}/requisition_public_links/bulk_destroy`, params);
  newField = (approvalTemplateId: number, params: { field: { body: string } }) =>
    RecruitApiClient.post(`${BASE_URL}/fields?requisition_form_id=${approvalTemplateId}`, params);
  addSystemField = (approvalTemplateId: number, systemFieldId: number) =>
    RecruitApiClient.post(
      `${BASE_URL}/system_fields?requisition_form_id=${approvalTemplateId}&system_field_id=${systemFieldId}`
    );
  getQuestions = (params: { entity_id: number; requisition_form_id: number }) =>
    RecruitApiClient.get<{ fields: IApprovalTemplateFields[] }>(`${BASE_URL}/fields`, params);
  deleteQuestion = (id: number, approvalTemplateId: number) =>
    RecruitApiClient.delete<{ errors: string }>(`${BASE_URL}/fields/${id}?requisition_form_id=${approvalTemplateId}`);
  cloneQuestion = (id: number, approvalTemplateId: number) =>
    RecruitApiClient.post(
      `${BASE_URL}/fields/${id}/clone?requisition_form_id=${approvalTemplateId}`
    );
  putQuestion = (id: number, approvalTemplateId: number, params: { field: { body: string } }) =>
    RecruitApiClient.put(
      `${BASE_URL}/fields/${id}?requisition_form_id=${approvalTemplateId}`,
      params
    );
  postArchiveApprovalForms = (params: {}) =>
    RecruitApiClient.post<{ requisition_ids: { failure: [], success: [] } }>(`${BASE_URL}/requisitions/bulk_archive`, params);
  postUnarchiveApprovalForms = (params: {}) =>
    RecruitApiClient.post<{ requisition_ids: { failure: [], success: [] } }>(`${BASE_URL}/requisitions/bulk_unarchive`, params);
  deleteTrashApprovalForms = (params: {}) =>
    RecruitApiClient.delete<{ requisition_ids: { failure: [], success: [] } }>(`${BASE_URL}/requisitions/bulk_destroy`, params);
  postArchiveApprovalTemplates = (params: {}) =>
    RecruitApiClient.post<{ success: string }>(`${BASE_URL}/requisition_forms/bulk_archive`, params);
  postUnarchiveApprovalTemplates = (params: {}) =>
    RecruitApiClient.post(`${BASE_URL}/requisition_forms/bulk_unarchive`, params);
  deleteTrashApprovalTemplates = (params: {}) =>
    RecruitApiClient.delete<{ success: string }>(`${BASE_URL}/requisition_forms/bulk_destroy`, params);
  getAssignableUsers = () => RecruitApiClient.get<{ assignable_users: IAssignableUsers[] }>(`${BASE_URL}/assignable_users`);
  getCSVData = (questionId: number, positionId: number) =>
    RecruitApiClient.get(`${BASE_URL}/system_fields/${questionId}/remote`, {
      position_id: positionId,
      new_position: false
    });
  getJobs = () => RecruitApiClient.get<{ jobs: IJob[] }>(`${BASE_URL}/jobs/jobs_for_requisitions`);
  getApplications = (jobId: number) =>
    RecruitApiClient.get<{ id: number; candidate_name: string }[]>(`${BASE_URL}/jobs/${jobId}/applications_for_requisition`);
  getFilters = (url: string, params: {}) =>
    RecruitApiClient.get(`${BASE_URL}/requisitions/${url}`, params);
}

export default new Api();
